/**
 * Backend API client.
 *
 * Runtime configuration comes from two sources:
 * - `import.meta.env.VITE_API_BASE_URL` baked at build time (Cloudflare Pages
 *   build env). Defaults to "" (same-origin) for local `vite dev` proxying.
 * - `X-Admin-Token` value held in `localStorage` under `admin-token`. The
 *   user is prompted on first load; we never embed this token in the bundle.
 *
 * All admin endpoints require the `X-Admin-Token` header (server side:
 * `adminTokenMiddleware`, see `apps/backend/src/admin/auth.ts`).
 */

import type { AddBanResponse, BanRecord, ListBansResponse } from "shared/api";

export type { AddBanResponse, BanRecord, ListBansResponse };

const BASE_URL: string = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "").replace(
  /\/$/,
  "",
);
const TOKEN_KEY = "admin-token";

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getAdminToken();
  if (!token) throw new ApiError(401, "admin token not set");
  const init: RequestInit = {
    method,
    headers: {
      "X-Admin-Token": token,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(`${BASE_URL}${path}`, init);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Non-JSON body (HTML 502 page etc.) — preserve raw text for diagnostics.
      parsed = { error: text.slice(0, 200) };
    }
  }
  if (!res.ok) {
    const msg =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String(parsed.error)
        : `HTTP ${String(res.status)}`;
    throw new ApiError(res.status, msg);
  }
  return parsed as T;
}

export function listBans(): Promise<ListBansResponse> {
  return request<ListBansResponse>("GET", "/admin/bans");
}

export function addBan(phone: string, reason: string): Promise<AddBanResponse> {
  return request<AddBanResponse>("POST", "/admin/bans", { phone, reason });
}

export function deleteBan(hash: string, v: 1 | 2): Promise<undefined> {
  return request<undefined>("DELETE", `/admin/bans/${hash}/${String(v)}`);
}
